import FullscreenPicture from './fullscreenPicture';
import anime from 'animejs';
import { isLessThan } from '../shared/mq';

export default class MosaicOverlayer {
  constructor(el) {
    if (!el) {
      return;
    }

    this.$el = el;
    this.items = this.$el.querySelectorAll('.u-mosaic-gallery__item-inner');

    this.overlayer = this.$el.querySelector('.u-mosaic-overlayer');
    this.wrapper = this.overlayer.querySelector('.u-mosaic-overlayer-wrapper');
    this.content = this.overlayer.querySelector('.u-mosaic-overlayer__content');
    this.closeButton = this.overlayer.querySelector('[data-mosaic-close]');
    this.nav = this.overlayer.querySelector('.u-mosaic-overlayer__nav');
    this.previousButton = this.overlayer.querySelector(
      '[data-mosaic-previous]'
    );
    this.nextButton = this.overlayer.querySelector('[data-mosaic-next]');

    this.url = window.location.href;
    window.history.replaceState('overlayer-closed', '', this.url);
    this.isOpen = false;
    this.index = 0;
    this.itemTitle = null;
    this.itemUrl = null;

    this.mouse = {
      x: 0,
      y: 0,
    };

    this.bind();
  }

  bind() {
    this.onItemClick = this.onItemClick.bind(this);
    this.onOverlayerClick = this.onOverlayerClick.bind(this);
    this.onNavClick = this.onNavClick.bind(this);
    this.onKeyUp = this.onKeyUp.bind(this);
    this.onPopstate = this.onPopstate.bind(this);
    this.onMouseMove = this.onMouseMove.bind(this);

    this.close = this.close.bind(this);

    this.items.forEach((item) =>
      item.addEventListener('click', this.onItemClick)
    );
    this.overlayer.addEventListener('click', this.onOverlayerClick);
    this.closeButton.addEventListener('click', this.close);
    this.previousButton.addEventListener('click', this.onNavClick);
    this.nextButton.addEventListener('click', this.onNavClick);

    window.addEventListener('keyup', this.onKeyUp);
    window.addEventListener('popstate', this.onPopstate);
  }

  onItemClick(e) {
    e.preventDefault();

    const el = e.currentTarget;

    this.feedOverlayer(el);
  }

  onOverlayerClick(e) {
    if (e.target === e.currentTarget) {
      this.close();
    }
  }

  onNavClick(e) {
    e.preventDefault();

    if (!e.currentTarget) {
      return;
    }

    if (e.currentTarget.hasAttribute('data-mosaic-previous')) {
      this.changeItem(false);
    } else {
      this.changeItem(true);
    }
  }

  onKeyUp(e) {
    if (
      !this.isOpen ||
      document.querySelector('.u-mosaic__picture__fullscreen')
    ) {
      return;
    }

    if (e.key === 'ArrowRight') {
      this.changeItem();
    } else if (e.key === 'ArrowLeft') {
      this.changeItem(false);
    } else if (e.key === 'Escape') {
      this.close();
    }
  }

  onPopstate(e) {
    if (window.location.href === this.url) {
      this.close(e, true);
    } else {
      const target = this.$el.querySelector(
        `.u-mosaic-gallery__item-inner[href="${window.location.href}"]`
      );

      this.feedOverlayer(target);
    }
  }

  changeItem(forwards = true) {
    this.fullscreen.destroy();

    if (forwards) {
      if (this.index === this.items.length - 1) {
        return;
      }

      this.index += 1;
    } else {
      if (this.index === 0) {
        return;
      }

      this.index -= 1;
    }

    const el = document.querySelector(`[data-mosaic-index="${this.index}"]`);

    anime({
      targets: this.content,
      opacity: 0,
      duration: 600,
      easing: 'easeInExpo',
      complete: () => {
        this.feedOverlayer(el);
        window.history.replaceState('overlayer-open', '', this.itemUrl);
      },
    });
  }

  feedOverlayer(el) {
    this.index = parseInt(el.dataset.mosaicIndex);
    this.itemUrl = el.href;
    this.itemTitle = el.querySelector('span').innerHTML;

    const id = el.dataset.mosaicId;
    const lang = el.dataset.mosaicLang;

    // Not working in localhost: use prod url to test
    // https://www.musees.uliege.be/galerieMosaiqueServlet?id=c_14447638&lang=fr
    const url = `/galerieMosaiqueServlet?id=${id}&lang=${lang}`;

    fetch(url)
      .then((response) => {
        return response.text();
      })
      .then((data) => {
        this.content.innerHTML = data;

        this.fullscreen = new FullscreenPicture(
          this.content.querySelector(
            '.u-mosaic__picture-container[data-fullscreen]'
          )
        );

        if (this.isOpen) {
          this.displayChangedContent();
        } else {
          this.open();
        }
      })
      .catch((error) => {
        console.error(error);
      });
  }

  open() {
    window.addEventListener('mousemove', this.onMouseMove);

    this.previousButton.disabled = this.index === 0;
    this.nextButton.disabled = this.index === this.items.length - 1;

    const tl = anime.timeline({
      begin: () => {
        this.overlayer.style.display = 'block';
        this.wrapper.style.opacity = 0;
        this.nav.style.opacity = 0;

        this.isAnimating = true;
        document.body.classList.add('no-scroll');

        window.history.pushState('overlayer-open', '', this.itemUrl);
      },
      complete: () => {
        this.isOpen = true;
        this.isAnimating = false;
        this.overlayer.classList.add('is-visible');
      },
    });

    tl.add({
      targets: this.overlayer,
      opacity: [0, 1],
      duration: 500,
      easing: 'easeOutExpo',
    })
      .add(
        {
          targets: this.wrapper,
          opacity: [0, 1],
          translateY: ['100%', '0%'],
          duration: 600,
          easing: 'easeOutExpo',
          complete: () => {
            this.wrapper.removeAttribute('style');
          },
        },
        '-=400'
      )
      .add({
        targets: this.nav,
        opacity: [0, 1],
        duration: 400,
        easing: 'easeOutExpo',
      });
  }

  displayChangedContent() {
    this.previousButton.disabled = this.index === 0;
    this.nextButton.disabled = this.index === this.items.length - 1;

    anime({
      targets: this.content,
      opacity: [0, 1],
      duration: 600,
      easing: 'easeOutExpo',
    });
  }

  close(e, isPopState = false) {
    if (!this.isOpen || this.isAnimating) {
      return;
    }

    window.removeEventListener('mousemove', this.onMouseMove);

    const tl = anime.timeline({
      easing: 'easeOutExpo',
      begin: () => {
        this.isAnimating = true;
      },
      complete: () => {
        document.body.classList.remove('no-scroll');
        this.overlayer.removeAttribute('style');
        this.overlayer.classList.remove('is-visible');
        this.previousButton.removeAttribute('style');
        this.nextButton.removeAttribute('style');

        this.isOpen = false;
        this.isAnimating = false;
        this.content.innerHTML = '';

        if (!isPopState) {
          // window.history.replaceState('overlayer-closed', '', this.url);
          window.history.back();
        }
      },
    });

    tl.add({
      targets: this.wrapper,
      opacity: 0,
      translateY: ['0%', '100%'],
      easing: 'easeInExpo',
      duration: 250,
    }).add(
      {
        targets: this.overlayer,
        opacity: 0,
        duration: 500,
      },
      '200'
    );
  }

  /*
   ** Button hover animation
   */

  onMouseMove(e) {
    if (isLessThan('large')) {
      return;
    }

    this.mouse.x = e.clientX;
    this.mouse.y = e.clientY;
  }
}
